import { useState, useCallback } from "react";
import { validateAccountNumber } from "@/api/projectsApi";
import { validateAccountNumberFormat } from "@/utils/validateAccountNumber";

export interface CustomerData {
  customerName: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  email?: string;
  ratePlan?: string;
  careEligible?: boolean;
  feraEligible?: boolean;
  cocEligible?: boolean;
  mailingAddress?: string;
  mailingCity?: string;
  mailingState?: string;
  mailingZipCode?: string;
}

export interface AccountValidationResult {
  isValid: boolean;
  customerData?: CustomerData;
  error?: string;
  zipCodeMatch?: boolean;
}

const useAccountValidation = (
  debounceMs: number = 500,
  skipDuplicateCheck: boolean = false,
  isSizingTool: boolean = false
) => {
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] =
    useState<AccountValidationResult | null>(null);

  const validateAccount = useCallback(
    async (
      accountNumber: string,
      zipCode?: string
    ): Promise<AccountValidationResult> => {
      const formatValidation = validateAccountNumberFormat(accountNumber);
      if (!formatValidation.valid) {
        return { isValid: false, error: formatValidation.message };
      }

      try {
        setIsValidating(true);
        const response = await validateAccountNumber(
          accountNumber,
          zipCode,
          isSizingTool
        );
        const rawData = response.data;

        const dataValues = rawData.dataValues || rawData;

        const customerData: CustomerData = {
          customerName: `${dataValues.Customer_First_Name || ""} ${
            dataValues.Customer_Last_Name || ""
          }`.trim(),
          address: dataValues.Address || "",
          city: dataValues.City || "",
          state: dataValues.State || "",
          zipCode: dataValues.Zip_Code || "",
          email: dataValues.Customer_Email || "",
          ratePlan: dataValues.Utility_Rate || "",
          careEligible: dataValues.CARE === "Y",
          feraEligible: dataValues.FERA === "Y",
          cocEligible: dataValues.isCoC === "Y",
          mailingAddress: dataValues.Mailing_Address || "",
          mailingCity: dataValues.Mailing_City || "",
          mailingState: dataValues.Mailing_State || "",
          mailingZipCode: dataValues.Mailing_Zip_Code || "",
        };

        let zipCodeMatch = true;
        if (zipCode && customerData.zipCode) {
          zipCodeMatch = customerData.zipCode === zipCode;
        }

        return {
          isValid: true,
          customerData,
          zipCodeMatch,
        };
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Account number not found. Please check and try again.";

        const isZipCodeError =
          errorMessage.toLowerCase().includes("zipcode") ||
          errorMessage.toLowerCase().includes("does not match");

        const isAlreadyExistsError =
          errorMessage.toLowerCase().includes("already exists") ||
          errorMessage.toLowerCase().includes("account number already");

        if (skipDuplicateCheck && isAlreadyExistsError) {
          return {
            isValid: true,
            customerData: undefined,
            zipCodeMatch: true,
          };
        }

        return {
          isValid: false,
          error: errorMessage,
          zipCodeMatch: isZipCodeError ? false : undefined,
        };
      } finally {
        setIsValidating(false);
      }
    },
    [skipDuplicateCheck, isSizingTool]
  );

  const debouncedValidate = useCallback(
    (() => {
      let timeoutId: ReturnType<typeof setTimeout>;

      return (accountNumber: string, zipCode?: string) => {
        clearTimeout(timeoutId);

        const formatValidation = validateAccountNumberFormat(accountNumber);
        if (!formatValidation.valid) {
          setValidationResult({
            isValid: false,
            error: formatValidation.message,
          });
          return;
        }

        const debounceTime = zipCode ? 1000 : debounceMs;

        timeoutId = setTimeout(async () => {
          if (zipCode && zipCode.length > 5) {
            return;
          }

          const result = await validateAccount(accountNumber, zipCode);

          setValidationResult(result);
        }, debounceTime);
      };
    })(),
    [validateAccount, debounceMs, skipDuplicateCheck, isSizingTool]
  );

  const clearValidation = useCallback(() => {
    setValidationResult(null);
  }, []);

  return {
    isValidating,
    validationResult,
    validateAccount: debouncedValidate,
    clearValidation,
  };
};

export default useAccountValidation;
